import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { Mrf } from '../state-man/models/mrf.model';
import { UiService } from './ui.service';
import { LoggerService } from './logger.service';
import { MrfPhoto } from '../state-man/models/mrf-photo.model';
import { PhotoService } from './photo.service';
import { ApiError } from '../utils/api-error';

@Injectable({
  providedIn: 'root'
})
export class MrfService {

  constructor(
    private apiService: ApiService,
    private uiService: UiService
  ) { }

  public async getMine () : Promise<Mrf[] | null> {   
    try {      
      const ret = await this.apiService.apiReturnsAny('get', 'mrf/mine/')       
      const data = (ret && Array.isArray(ret)) ? Mrf.buildMany(ret) : null
      if (!data) return null      
      return data      
    } catch (error) {
      LoggerService.log(error + 'mrf/mine/')   
      await this.uiService.showToast('An unexpected error occurred and has been sent to the support team.')    
      return null
    }    
  }  

  public async addMrf(mrf: Mrf) : Promise<Mrf | null> {
    try {
      const body = Mrf.toApiPayload(mrf)
      return await this.apiService.apiReturnsAny('post', 'mrf/', body, 'application/json')
    } catch (error) {
      LoggerService.log(error + 'post mrf/')   
      await this.uiService.showToast('An unexpected error occurred and has been sent to the support team.')    
      return null
    }    
  }  

  public async updateMrf(mrf: Mrf) : Promise<Mrf | null> {
    try {      
      const body = {data: Mrf.toApiPayload(mrf)}
      const ret = await this.apiService.apiReturnsAny('put', 'mrf/' + mrf.id, body, 'application/json')         
      return ret
    } catch (error) {
      LoggerService.log(error + 'put mrf/')   
      await this.uiService.showToast('An unexpected error occurred and has been sent to the support team.')    
      return null
    }    
  }  

  public async deleteMrf(mrf: Mrf) : Promise<any> {
    try {
      const body = Mrf.toApiPayload(mrf)
      const ret = await this.apiService.apiReturnsAny('delete', 'mrf/' + mrf.id, body, 'application/json')                     
      return ret
    } catch (error) {    
      LoggerService.log(error + 'delete mrf/')   
      await this.uiService.showToast('An unexpected error occurred and has been sent to the support team.')      
      return null
    }    
  }  
  
  // One line item photo. 'conflict' when the server won't take it (its line
  // is gone or the MRF is completed): the app drops it then. null when it
  // didn't get through and should be sent again later.
  public async addPhoto(line_uuid: string, photo: MrfPhoto) : Promise<MrfPhoto | 'conflict' | null> {
    try {
      const body = new FormData()
      const base64Image = photo.base64.split(';base64,').pop()
      const blob = PhotoService.b64toBlob(base64Image, 'image/jpeg')
      body.append('media_file', blob, photo.uuid + '.jpg')
      body.append('uuid', photo.uuid)
      body.append('line_uuid', line_uuid)
      const ret = await this.apiService.apiReturnsAny('post', 'mrf/photos', body, 'multipart/form-data')
      return ret ? MrfPhoto.buildOne(ret) : null
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) return 'conflict'
      LoggerService.log(error + 'post mrf/photos')
      return null
    }
  }

  // Full size, for a photo this device doesn't have. Online only.
  public async getPhoto(id: number) : Promise<{ photo_type: string, photo_data: string } | null> {
    try {
      return await this.apiService.apiReturnsAny('get', 'mrf/photos/' + id)
    } catch (error) {
      LoggerService.log(error + 'get mrf/photos/')
      return null
    }
  }

  public async deletePhoto(uuid: string) : Promise<boolean> {
    try {
      await this.apiService.apiReturnsAny('delete', 'mrf/photos/' + uuid, {}, 'application/json')
      return true
    } catch (error) {
      // Not theirs to delete, or not a photo: nothing a retry would change.
      if (error instanceof ApiError && [400, 403, 404, 409].includes(error.status ?? 0)) return true
      LoggerService.log(error + 'delete mrf/photos/')
      return false
    }
  }

}
